// The session's path through cluster space, as data.
//
// One bounded query per session: every prompt the session issued, with the
// cluster the clustering pipeline assigned it, time-ascending. Two surfaces
// read it — the trace-detail hero draws it as the journey band (an index axis
// with arcs for returns), and the lanes view projects the same rows onto its
// turn axis as the topic band. Both go through runQuery's result cache, so
// whichever reader arrives second pays nothing.
//
// The FETCH is session-scoped; the derived journey is per TRACE (the focal
// marker belongs to whichever trace is open), which is why the build sits in a
// memo outside the effect: paging between traces of one session must move the
// marker without refetching.
import { useEffect, useMemo, useRef, useState } from "react";
import { buildJourney } from "./traceAnatomy.js";
import { runQuery, rowsToObjects } from "./analyticsClient.js";
import { parseServerTimestamp } from "./traceUtils.js";

const JOURNEY_ROW_LIMIT = 1500;
// The sequence repeats verbatim for every trace opened in one session, so let
// the shared result cache hold it past runQuery's default 30s.
const JOURNEY_STALE_MS = 5 * 60 * 1000;

export function journeySpec(sessionId) {
  return {
    root: {
      alias: "p", entity: "ENTITY_TYPE_PROMPT",
      stepFilter: [],
      children: [{
        edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
        mode: "JOIN_MODE_INNER",
        target: {
          alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
          children: [{
            edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
            mode: "JOIN_MODE_LEFT",
            target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER" },
          }],
        },
      }],
    },
    timeRange: { relative: { durationSeconds: String(90 * 86400) } },
    filter: { pred: {
      field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" },
      op: "OPERATOR_EQ", value: sessionId,
    }},
    output: {
      dimensions: [
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, outputAlias: "event_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
        { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, outputAlias: "cluster_id" },
        { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" }, outputAlias: "name" },
      ],
    },
    orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }],
    limit: JOURNEY_ROW_LIMIT,
  };
}

/**
 * The session's journey, keyed on the open trace.
 *
 * `truncated` means the row cap bound: the rows are time-ASCENDING, so the cap
 * keeps the session's OLDEST prompts and drops its most recent ones — a
 * consumer that draws the tail of the session has to say so.
 *
 * @returns { status: "idle"|"loading"|"ready"|"empty", truncated, journey }
 */
export function useSessionJourney(trace) {
  const sessionId = trace?.sessionId ? String(trace.sessionId) : null;
  const focalTsMs = trace?.timestamp instanceof Date ? trace.timestamp.getTime() : Number(trace?.timestamp) || null;
  const [state, setState] = useState({ status: "idle", rows: null, truncated: false });
  const runIdRef = useRef(0);
  useEffect(() => {
    if (!sessionId) { setState({ status: "empty", rows: null, truncated: false }); return undefined; }
    const runId = ++runIdRef.current;
    let alive = true;
    setState({ status: "loading", rows: null, truncated: false });
    (async () => {
      const rows = rowsToObjects(await runQuery(journeySpec(sessionId), { staleMs: JOURNEY_STALE_MS }))
        .map((r) => ({
          eventId: String(r.event_id || ""),
          tsMs: (() => { const t = parseServerTimestamp(r.event_time); return Number.isFinite(t) ? t : 0; })(),
          clusterId: r.cluster_id == null || r.cluster_id === "" ? null : Number(r.cluster_id),
          clusterName: String(r.name || ""),
        }));
      if (!alive || runId !== runIdRef.current) return;
      setState({ status: "ready", rows, truncated: rows.length >= JOURNEY_ROW_LIMIT });
    })().catch((err) => {
      if (!alive || runId !== runIdRef.current) return;
      console.warn("[session-journey] unavailable:", err?.message || err);
      setState({ status: "empty", rows: null, truncated: false });
    });
    return () => { alive = false; };
  }, [sessionId]);
  const journey = useMemo(
    () => (state.rows
      ? buildJourney(state.rows, { focalEventId: trace?.id ? String(trace.id) : null, focalTsMs })
      : null),
    [state.rows, trace?.id, focalTsMs],
  );
  return { status: state.status, truncated: state.truncated, journey };
}
